import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/services/supabase";
import type { DanceLabEvent, DanceLabScheduleItem, DanceLabPricingTier } from "@/data/danceLabEvents";

interface DanceLabEventRow {
  id: string;
  slug: string;
  city: string;
  country: string;
  dates: string;
  year: number;
  description: string;
  subtitle: string | null;
  level: string | null;
  total_hours: string | null;
  venue: string | null;
  selection_note: string | null;
  hero_image: string | null;
  og_image: string | null;
  schedule: DanceLabScheduleItem[];
  schedule_footer: string | null;
  pricing: DanceLabPricingTier[];
  pricing_note: string;
  application_deadline: string;
  spots_available: number;
  is_active: boolean;
  display_order: number;
}

function rowToEvent(row: DanceLabEventRow): DanceLabEvent {
  return {
    slug: row.slug,
    city: row.city,
    country: row.country,
    dates: row.dates,
    year: row.year,
    description: row.description,
    subtitle: row.subtitle ?? undefined,
    level: row.level ?? undefined,
    totalHours: row.total_hours ?? undefined,
    venue: row.venue ?? undefined,
    selectionNote: row.selection_note ?? undefined,
    heroImage: row.hero_image ?? undefined,
    ogImage: row.og_image ?? undefined,
    schedule: row.schedule,
    scheduleFooter: row.schedule_footer ?? undefined,
    pricing: row.pricing,
    pricingNote: row.pricing_note,
    applicationDeadline: row.application_deadline,
    spotsAvailable: row.spots_available,
  };
}

export function useDanceLabEvents() {
  return useQuery({
    queryKey: ["dance-lab-events"],
    queryFn: async (): Promise<DanceLabEvent[]> => {
      const { data, error } = await supabase
        .from("dance_lab_events" as any)
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return (data as unknown as DanceLabEventRow[]).map(rowToEvent);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useDanceLabEvent(slug: string | undefined) {
  return useQuery({
    queryKey: ["dance-lab-events", slug],
    queryFn: async (): Promise<DanceLabEvent | null> => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("dance_lab_events" as any)
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return rowToEvent(data as unknown as DanceLabEventRow);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}
